"use client";

import {
  Box, Paper, Typography, Grid, TextField, Button,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableSortLabel, CircularProgress, Pagination, Chip,
  Dialog, DialogContent, DialogActions, MenuItem,
  IconButton, Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { maskPhone } from "../../helper";

const fmt = (v) => `${Number(v || 0).toLocaleString("en-IN")}`;

const WALLET_COLS = [
  { key: "winning_balance",     label: "Winning",      color: "#1976d2", bg: "#e3f2fd" },
  { key: "promotional_balance", label: "Promo",        color: "#7b1fa2", bg: "#f3e5f5" },
  { key: "cash_deposite",       label: "Cash Dep.",    color: "#388e3c", bg: "#e8f5e9" },
  { key: "loan_amount",         label: "Loan Limit",   color: "#e65100", bg: "#fff3e0" },
  { key: "loan_distributed",    label: "Loan Used",    color: "#c62828", bg: "#ffebee" },
];

const WALLET_OPTIONS = [
  { value: "winning_balance",     label: "Winning Balance"    },
  { value: "promotional_balance", label: "Promo Balance"      },
  { value: "cash_deposite",       label: "Cash Deposit"       },
  { value: "loan_distributed",    label: "Loan Distributed"   },
];

const SumCard = ({ label, value, color, bg }) => (
  <Paper sx={{ p: 2, background: bg, borderRadius: 2, textAlign: "center" }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
    <Typography variant="h6" fontWeight={800} sx={{ color, mt: 0.5 }}>{fmt(value)}</Typography>
  </Paper>
);

export default function WalletManager() {
  const token = useSelector((s) => s.loginSlice.token);

  const [search, setSearch]       = useState("");
  const [users, setUsers]         = useState([]);
  const [sums, setSums]           = useState({});
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [sortBy, setSortBy]       = useState("winning_balance");
  const [sortOrder, setSortOrder] = useState(-1);
  const [loading, setLoading]     = useState(false);
  const [searched, setSearched]   = useState(false);

  // Add Credit dialog
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [walletType, setWalletType]     = useState("winning_balance");
  const [creditAmt, setCreditAmt]       = useState("");
  const [remarks, setRemarks]           = useState("");
  const [submitting, setSubmitting]     = useState(false);
  const [alertMsg, setAlertMsg]         = useState(null);

  const SIZE = 50;

  const fetchData = useCallback(async (pg = 1, sb = sortBy, so = sortOrder, q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pg, size: SIZE, sort_by: sb, sort_order: so,
        ...(q.trim() ? { search: q.trim() } : {}),
      });
      const res = await axiosRequest("GET", `/wallet-manager?${params}`, token);
      if (res?.status === "success") {
        setUsers(res.data.users || []);
        setSums(res.data.sums   || {});
        setTotal(res.data.total || 0);
        setPage(pg);
        setSearched(true);
      }
    } finally {
      setLoading(false);
    }
  }, [token, sortBy, sortOrder, search]);

  useEffect(() => { fetchData(); }, []);

  const handleSearch = () => fetchData(1, sortBy, sortOrder, search);

  const handleReset = () => {
    setSearch("");
    setSortBy("winning_balance");
    setSortOrder(-1);
    setPage(1);
    setSearched(false);
    fetchData(1, "winning_balance", -1, "");
  };

  const handleSort = (col) => {
    const newOrder = sortBy === col ? (sortOrder === -1 ? 1 : -1) : -1;
    setSortBy(col);
    setSortOrder(newOrder);
    fetchData(1, col, newOrder, search);
  };

  const handlePageChange = (_, pg) => fetchData(pg, sortBy, sortOrder, search);

  const openDialog = (user) => {
    setSelectedUser(user);
    setWalletType("winning_balance");
    setCreditAmt("");
    setRemarks("");
    setAlertMsg(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (submitting) return;
    setDialogOpen(false);
    setSelectedUser(null);
    setAlertMsg(null);
  };

  const handleAddCredit = async () => {
    if (!creditAmt || Number(creditAmt) === 0) {
      setAlertMsg({ type: "error", text: "Enter a valid amount" });
      return;
    }
    setSubmitting(true);
    setAlertMsg(null);
    try {
      const res = await axiosRequest("POST", "/wallet-credit", token, {
        user_id:     selectedUser._id,
        wallet_type: walletType,
        amount:      Number(creditAmt),
        remarks,
      });
      if (res?.status === "success") {
        setAlertMsg({ type: "success", text: res.message || "Credit added successfully" });
        // refresh the row
        fetchData(page, sortBy, sortOrder, search);
        setTimeout(() => closeDialog(), 1200);
      } else {
        setAlertMsg({ type: "error", text: res?.message || "Failed to add credit" });
      }
    } catch {
      setAlertMsg({ type: "error", text: "Something went wrong" });
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(total / SIZE);
  const selectedWalletLabel = WALLET_OPTIONS.find((o) => o.value === walletType)?.label || "";

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <AccountBalanceWalletIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>Wallet Manager</Typography>
      </Box>

      {/* ── SEARCH ── */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth size="small"
              label="Search by Name / Phone / Player ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </Grid>
          <Grid item xs="auto">
            <Button variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={handleSearch} disabled={loading}
            >Search</Button>
          </Grid>
          <Grid item xs="auto">
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset} disabled={loading}>
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ── WALLET SUMS ── */}
      {searched && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={4} md={2}>
            <Paper sx={{ p: 2, background: "#f5f7fa", borderRadius: 2, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Total Users</Typography>
              <Typography variant="h6" fontWeight={800} mt={0.5}>{total.toLocaleString("en-IN")}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}><SumCard label="Total Winning"    value={sums.total_winning}     color="#1976d2" bg="#e3f2fd" /></Grid>
          <Grid item xs={6} sm={4} md={2}><SumCard label="Total Promo"      value={sums.total_promo}       color="#7b1fa2" bg="#f3e5f5" /></Grid>
          <Grid item xs={6} sm={4} md={2}><SumCard label="Total Cash Dep."  value={sums.total_cash}        color="#388e3c" bg="#e8f5e9" /></Grid>
          <Grid item xs={6} sm={4} md={2}><SumCard label="Total Loan Limit" value={sums.total_loan_amount} color="#e65100" bg="#fff3e0" /></Grid>
          <Grid item xs={6} sm={4} md={2}><SumCard label="Total Loan Used"  value={sums.total_loan_dist}   color="#c62828" bg="#ffebee" /></Grid>
        </Grid>
      )}

      {/* ── TABLE ── */}
      {loading && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}

      {!loading && users.length > 0 && (
        <>
          <Paper elevation={3} sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "linear-gradient(135deg,#1e3c72,#2a5298)" }}>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                    <TableSortLabel active={sortBy === "name"}
                      direction={sortBy === "name" ? (sortOrder === 1 ? "asc" : "desc") : "desc"}
                      onClick={() => handleSort("name")}
                      sx={{ color: "#fff !important", "& .MuiTableSortLabel-icon": { color: "#fff !important" } }}>
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Phone</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Player ID</TableCell>
                  {WALLET_COLS.map(({ key, label }) => (
                    <TableCell key={key} align="right" sx={{ color: "#fff", fontWeight: 700 }}>
                      <TableSortLabel active={sortBy === key}
                        direction={sortBy === key ? (sortOrder === 1 ? "asc" : "desc") : "desc"}
                        onClick={() => handleSort(key)}
                        sx={{ color: "#fff !important", "& .MuiTableSortLabel-icon": { color: "#fff !important" } }}>
                        {label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u, i) => (
                  <TableRow key={u._id} hover sx={{ "&:nth-of-type(even)": { background: "#fafbfc" } }}>
                    <TableCell sx={{ color: "text.disabled", fontSize: 12 }}>{(page - 1) * SIZE + i + 1}</TableCell>
                    <TableCell><Typography variant="body2" fontWeight={600}>{u.name || "—"}</Typography></TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{maskPhone(u.phone) || "—"}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{u.playerId || "—"}</TableCell>
                    {WALLET_COLS.map(({ key, color, bg }) => {
                      const val = Number(u[key] || 0);
                      return (
                        <TableCell key={key} align="right">
                          {val > 0
                            ? <Chip label={fmt(val)} size="small" sx={{ background: bg, color, fontWeight: 700, fontSize: 12 }} />
                            : <Typography variant="body2" color="text.disabled">—</Typography>
                          }
                        </TableCell>
                      );
                    })}
                    <TableCell align="center">
                      <Button size="small" variant="outlined" color="success"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => openDialog(u)}
                        sx={{ fontSize: 11, whiteSpace: "nowrap" }}
                      >
                        Add Credit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" shape="rounded" />
            </Box>
          )}
        </>
      )}

      {!loading && searched && users.length === 0 && (
        <Paper sx={{ p: 6, textAlign: "center", borderRadius: 3 }}>
          <Typography variant="h6" color="text.secondary">No users found</Typography>
          <Typography variant="body2" color="text.disabled" mt={1}>Try adjusting your search.</Typography>
        </Paper>
      )}

      {/* ── ADD CREDIT DIALOG ── */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ background: "linear-gradient(135deg,#1e3c72,#2a5298)", px: 3, py: 2, color: "#fff",
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {Number(creditAmt) < 0 ? "Deduct from Wallet" : "Add Wallet Credit"}
            </Typography>
            {selectedUser && (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {selectedUser.name || "—"} &nbsp;|&nbsp; {maskPhone(selectedUser.phone) || selectedUser.playerId || ""}
              </Typography>
            )}
          </Box>
          <IconButton onClick={closeDialog} sx={{ color: "#fff" }} disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 3, pb: 1 }}>
          {alertMsg && (
            <Alert severity={alertMsg.type} sx={{ mb: 2 }}>{alertMsg.text}</Alert>
          )}

          {/* Current balances */}
          {selectedUser && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2.5, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                Current Balances
              </Typography>
              <Grid container spacing={1}>
                {[...WALLET_OPTIONS, { value: "loan_amount", label: "Loan Limit" }]
                  .filter((o, i, arr) => arr.findIndex(x => x.value === o.value) === i)
                  .map(({ value, label }) => (
                  <Grid item xs={6} key={value}>
                    <Box sx={{ p: 1, borderRadius: 1.5, background: value === walletType ? "#e3f2fd" : "#f5f7fa" }}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: value === walletType ? "#1565c0" : "text.primary" }}>
                        {fmt(selectedUser[value] || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          <TextField
            select fullWidth size="small" label="Wallet Type"
            value={walletType} onChange={(e) => setWalletType(e.target.value)} sx={{ mb: 2 }}
          >
            {WALLET_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth size="small" type="number" label="Amount ()  — use minus to deduct"
            value={creditAmt} onChange={(e) => setCreditAmt(e.target.value)}
            inputProps={{ step: 1 }} sx={{ mb: 2 }}
          />

          <TextField
            fullWidth size="small" label="Remarks (optional)" multiline rows={2}
            value={remarks} onChange={(e) => setRemarks(e.target.value)}
          />

          {creditAmt !== "" && Number(creditAmt) !== 0 && (
            <Paper sx={{ mt: 2, p: 1.5, borderRadius: 2, background: Number(creditAmt) < 0 ? "#ffebee" : "#e8f5e9" }}>
              <Typography variant="body2" fontWeight={700} color={Number(creditAmt) < 0 ? "error.dark" : "success.dark"}>
                {Number(creditAmt) < 0 ? "−" : "+"} {Math.abs(Number(creditAmt)).toLocaleString("en-IN")} → {selectedWalletLabel}
              </Typography>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={closeDialog} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            color={Number(creditAmt) < 0 ? "error" : "success"}
            onClick={handleAddCredit}
            disabled={submitting || !creditAmt || Number(creditAmt) === 0}
          >
            {submitting
              ? <CircularProgress size={20} color="inherit" />
              : Number(creditAmt) < 0 ? "Confirm Deduction" : "Confirm Credit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
